/* PLAN — the week the coach laid out, read-only.

   Everything on this screen comes from `GET /plan`, which the coach writes over
   MCP (`log_plan`) and nothing here can change. There is no composer, no status
   toggle and no delete: marking a meal done is a sentence said to the coach, and
   a button here would be a second, silently diverging way to say it.

   Day-first rather than list-first. `/plan` returns a fifteen-day window
   (today−7 … today+7) in one response, and rendering all of it as one flat feed
   buries the only question the screen exists to answer — "what am I eating
   today" — under a week of history. So the dates become a chip strip and exactly
   one day's items are on screen at a time.

   Every rule below that is arithmetic rather than markup is an exported pure
   function, tested in `Plan.test.ts`; the component is a thin renderer over
   them. Vitest only collects `.test.ts`, so a rule left inside the JSX is a rule
   nothing checks. */

import { useEffect, useRef, useState } from 'react'
import { Alert, Pill, Window } from '../ds'
import {
  errorMessage,
  getPlan,
  getSavedMeals,
  getTargets,
  type PlanItem,
  type SavedMeal,
} from '../api'
import { monoDate } from './format'
import '../styles/plan.css'

/* ── Titles ──────────────────────────────────────────────────────────────── */

/**
 * Whether a candidate slot label has a parenthesis it never closed.
 *
 * Only an UNCLOSED `(` can hide a colon: it means the colon that follows is
 * inside an aside rather than between the slot and the body. A stray `)` with
 * no opener is just a character the coach typed, and the colon after it is a
 * real separator — so the test counts openers against closers rather than
 * demanding they balance, and rejects only the first case. Rejecting the second
 * as well would take a readable split away from a title over a typo.
 */
function hasUnclosedParen(text: string): boolean {
  let depth = 0
  for (const char of text) {
    if (char === '(') depth += 1
    // Floored at zero so a leading `)` cannot pay for a later `(` and make a
    // genuinely unclosed opener look closed.
    else if (char === ')' && depth > 0) depth -= 1
  }
  return depth > 0
}

/**
 * Split a plan title into its slot label and its body.
 *
 * The coach writes titles as `"<slot>: <body>"` — `"Breakfast: B1 Protein
 * yogurt bowl"`, `"COOK NIGHT (20 min): D1 Smash burger bowl - MAKE DOUBLE"` —
 * and the slot is what belongs in the window's titlebar. The split is on the
 * FIRST colon that is not inside a parenthesis: a body may legitimately contain
 * more of them (`"Lunch: L2 bowl: no olives"`), and splitting on the last would
 * move half the meal into the titlebar.
 *
 * The parenthesis clause is the whole reason this is a loop. Titles are free
 * text and the coach already writes parenthesised asides in the slot — the live
 * `"COOK NIGHT (25 min)"` is one — so a parenthesised clock time is as plausible
 * as a duration. Taking the first colon unconditionally turned `"Snack (3:30pm):
 * Yasso bar"` into a titlebar reading `SNACK (3` and a meal line starting
 * `30pm)`, which is a card the user cannot read rather than one they disagree
 * with. A candidate whose left-hand side has an unclosed `(` is therefore
 * skipped and the next colon tried.
 *
 * Nothing in the API guarantees this shape, so both degenerate halves fall back
 * to showing the title exactly as it arrived. An empty slot would render a blank
 * titlebar; an empty body would render a card with nothing in it. Neither is a
 * thing the user can act on, and a title that does not follow the convention is
 * still perfectly readable whole. A degenerate half ends the search rather than
 * advancing it: `"Breakfast:"` has no body at any colon, and hunting further
 * would only find a worse split of a title that has none.
 *
 * Exported for `Plan.test.ts`.
 */
export function splitSlot(title: string): { slot: string | null; body: string } {
  const whole = title.trim()

  for (let colon = whole.indexOf(':'); colon !== -1; colon = whole.indexOf(':', colon + 1)) {
    const left = whole.slice(0, colon)
    if (hasUnclosedParen(left)) continue

    const slot = left.trim()
    const body = whole.slice(colon + 1).trim()
    if (!slot || !body) break
    return { slot, body }
  }
  return { slot: null, body: whole }
}

/* ── Days ────────────────────────────────────────────────────────────────── */

/**
 * Collect items into one group per date, first-seen order.
 *
 * Deliberately not a sort. `/plan` is documented as ascending by `(date, id)`
 * and the MCP `get_plan` tool serves the identical sequence, so re-ordering here
 * would let the screen and the coach mean different things by "the first meal of
 * the day". A response that arrives in some other order is rendered in that
 * order; the fix for a wrong order is on the server, where the coach would see
 * it too.
 *
 * Exported for `Plan.test.ts`.
 */
export function groupByDate(items: PlanItem[]): { date: string; items: PlanItem[] }[] {
  const groups: { date: string; items: PlanItem[] }[] = []
  // Index alongside the array so a date the server interleaved rejoins its own
  // group rather than opening a second one, without the group moving.
  const byDate = new Map<string, PlanItem[]>()

  for (const item of items) {
    let bucket = byDate.get(item.date)
    if (!bucket) {
      bucket = []
      byDate.set(item.date, bucket)
      groups.push({ date: item.date, items: bucket })
    }
    bucket.push(item)
  }
  return groups
}

/* ── Details ─────────────────────────────────────────────────────────────── */

/**
 * The key lifted out of `details` and shown once per day rather than per item.
 *
 * The coach hangs the day's total off whichever row it finished writing — in
 * practice the snack — so rendering it inside that card would claim a Yasso bar
 * was 1479 calories. `dayTotalOf` puts it in the day footer instead.
 */
const DAY_TOTAL_KEY = 'day_total'

/** The one key that is pinned to the top of a card rather than sorted into it. */
const LEAD_KEY = 'macros'

/**
 * One detail value as a display string.
 *
 * Shared with `dayTotalOf` so a total written as a number and a total written as
 * a string reach the screen looking the same. `null`/`undefined` are the
 * caller's business — they mean "no row at all", not "a row reading null".
 */
function detailValue(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value)
}

/** A snake_case key as a mono label: `saved_meal` -> `SAVED MEAL`. */
function labelOf(key: string): string {
  return key.replaceAll('_', ' ').toUpperCase()
}

/**
 * Turn a plan item's free-form `details` blob into label/value rows.
 *
 * `details` is jsonb the coach wrote by hand and its shape is not fixed, so
 * there is no schema to lay out against — every key is rendered, and the only
 * decision is the order.
 *
 * That order is alphabetical, with `macros` pinned first. It has to be *some*
 * total order rather than the key order of the object, because jsonb does not
 * preserve insertion order: Postgres stores the keys sorted by (length, bytes)
 * and hands them back that way, so "the order the coach wrote them" does not
 * survive the round trip and reading the object's own key order would produce an
 * arrangement that is arbitrary AND unstable across a rewrite. Alphabetical is
 * the only deterministic choice available. `macros` leads because it is the one
 * key present on nearly every meal and the one the user is looking for.
 *
 * `null` and `undefined` values are dropped rather than rendered: a key the
 * coach left empty is not a fact about the meal. The empty string is kept — that
 * is a value someone wrote.
 *
 * Each row carries its original `key` as well as the rendered `label`, because
 * `labelOf` is lossy: it uppercases and maps `_` to a space, so `saved_meal` and
 * `Saved Meal` are two keys with one label. The label is therefore not safe to
 * identify a row by, and the component keys its React list off `key` instead — a
 * list with two identical React keys drops a row. A blob the coach writes by hand
 * will almost certainly never hold both; carrying the key it already has costs
 * nothing and removes the question.
 *
 * `omit` names keys the CALLER has already rendered better elsewhere, and is
 * empty by default so a card with nothing to supersede these rows is unchanged.
 * Its one use today is the saved-meal join: once a card carries a real recipe and
 * real macros, `saved_meal` only repeats the name in the title and `macros` is
 * truncated prose the joined figures replace. Which of the two a given card has
 * actually replaced is `supersededKeys`' decision, made per card rather than
 * hardcoded here — a card with no saved meal behind it, or one whose saved meal
 * turned out to be empty, still shows both, and that is the fallback the whole
 * screen used to be.
 *
 * Exported for `Plan.test.ts`.
 */
export function detailRows(
  details: Record<string, unknown> | null,
  omit: readonly string[] = [],
): { key: string; label: string; value: string }[] {
  if (!details) return []

  const keys = Object.keys(details)
    .filter(
      (key) =>
        key !== DAY_TOTAL_KEY &&
        !omit.includes(key) &&
        details[key] !== null &&
        details[key] !== undefined,
    )
    .sort((a, b) => {
      if (a === LEAD_KEY) return -1
      if (b === LEAD_KEY) return 1
      return a.localeCompare(b)
    })

  return keys.map((key) => ({
    key,
    label: labelOf(key),
    value: detailValue(details[key]),
  }))
}

/**
 * The day's total, lifted out of whichever item carries it.
 *
 * `day_total` is a property of the day, but the API has nowhere to put one: the
 * response is a flat list of items and the coach attaches the total to whatever
 * row it was writing when it finished the day. So the whole day is searched
 * rather than a particular slot, and the FIRST one found wins — two disagreeing
 * totals is a coaching mistake to fix in the plan, not something a screen can
 * arbitrate, and picking the last would silently prefer whichever row sorted
 * later for reasons unrelated to the total.
 *
 * Exported for `Plan.test.ts`.
 */
export function dayTotalOf(items: PlanItem[]): string | null {
  for (const item of items) {
    const total = item.details?.[DAY_TOTAL_KEY]
    if (total !== null && total !== undefined) return detailValue(total)
  }
  return null
}

/* ── The saved-meal join ─────────────────────────────────────────────────── */

/** `details.saved_meal` — the coach's own pointer at the library, when it wrote one. */
const SAVED_MEAL_KEY = 'saved_meal'

/**
 * The recipe a resolved meal actually has, trimmed, or null for "no recipe".
 *
 * `saved_meals.description` is nullable and is free text someone typed, so the
 * distinction between "no recipe" and "a recipe that renders as nothing" is not
 * the same as the distinction between null and non-null. A description of `" "`
 * is truthy: it made `supersededKeys` drop the card's `SAVED MEAL` row and the
 * card render an empty `.milo-plan-recipe` div in its place, leaving strictly
 * less on screen than before the join existed — the exact failure
 * `supersededKeys` exists to prevent, arriving through the one input it did not
 * check.
 *
 * One function rather than a `.trim()` in each place, because the two places are
 * a claim and its justification: the card renders a recipe, `supersededKeys`
 * deletes a row on the grounds that the card rendered one. If they ever disagree
 * about what a recipe is, the row goes and nothing replaces it.
 *
 * Trimmed rather than merely tested, so the card gets the string it should
 * actually draw — a description stored with a leading newline would otherwise
 * open the recipe block with a blank line.
 *
 * Exported for `Plan.test.ts`.
 */
export function recipeText(saved: SavedMeal | null): string | null {
  const recipe = saved?.description?.trim()
  return recipe ? recipe : null
}

/**
 * The detail rows this card's join actually made redundant. See `detailRows`'s
 * `omit`.
 *
 * The question is what the join PRODUCED, not whether it matched, and those are
 * different questions because every column it produces from is nullable: all five
 * macros on `saved_meals` are, and so is `description`. A resolved meal can
 * therefore carry nothing at all. Suppressing on the match alone — which is what
 * a flat constant does — takes the prose `macros` row and the `saved_meal` row
 * off a card that then renders neither a recipe nor a figure in their place,
 * leaving strictly less on screen than before the join existed. The partial case
 * is worse than the empty one: a meal with only `calories` stored would delete a
 * prose row naming four other macros in exchange for one number.
 *
 * So each key is dropped only against the thing that replaces it. `macros` prose
 * goes when there is at least one real figure above it — one figure is still a
 * figure, and the card shows it. `saved_meal` merely repeats the title's name, so
 * anything the join rendered supersedes it: a figure, or a recipe. What counts
 * as a recipe is `recipeText`'s answer, not `description`'s truthiness — an
 * empty or all-whitespace description is not one, the card renders nothing for
 * it, so neither does this.
 *
 * Latent rather than live today: all 16 production `saved_meals` rows have a
 * description and all five macros, so every one of them returns both keys, which
 * is what the constant did. It is the next uncounted meal someone saves that the
 * constant loses a card over.
 *
 * Exported for `Plan.test.ts`.
 */
export function supersededKeys(
  saved: SavedMeal | null,
  figures: { label: string; value: string }[],
): readonly string[] {
  if (!saved) return []

  const keys: string[] = []
  if (figures.length > 0 || recipeText(saved) !== null) keys.push(SAVED_MEAL_KEY)
  if (figures.length > 0) keys.push(LEAD_KEY)
  return keys
}

/**
 * Whether a character can be part of a word, for `occursOnBoundary`.
 *
 * Letters and digits in any script, not `[a-z0-9]`: a name adjacent to an
 * accented letter is inside a word just as surely as one adjacent to an ASCII
 * one, and the ASCII-only test would silently wave it through.
 */
function isWordCharacter(char: string): boolean {
  return char !== '' && /[\p{L}\p{N}]/u.test(char)
}

/**
 * Whether `needle` occurs in `haystack` with a non-word character on both sides.
 *
 * Both already lowercased by the caller. EVERY occurrence is tried, not just the
 * first: `"Lunch: veggie bowl with Egg"` buries one `egg` inside `veggie` and
 * stands another on its own, and stopping at the first would reject a title that
 * genuinely names the meal.
 *
 * Start and end of string count as boundaries — there is no adjacent character
 * there to be part of a word.
 *
 * Conservative by design, and consistent with the stance `resolveSavedMeal`
 * takes everywhere else. Saved-meal names may begin or end with punctuation —
 * `"Coffee latte (daily)"` and `"S1 Yogurt + dark chocolate"` are both live —
 * and a name whose own edge character is not a word character can only ever be
 * REJECTED by an adjacent-word-character test, never falsely accepted. So the
 * check can lose a match it should have kept only in the case where the name
 * itself ends mid-word, which no name does.
 */
function occursOnBoundary(haystack: string, needle: string): boolean {
  for (let at = haystack.indexOf(needle); at !== -1; at = haystack.indexOf(needle, at + 1)) {
    const before = at === 0 ? '' : haystack[at - 1]
    const after = haystack[at + needle.length] ?? ''
    if (!isWordCharacter(before) && !isWordCharacter(after)) return true
  }
  return false
}

/**
 * Slot labels that mark a row a chore rather than something eaten.
 *
 * Matched with `startsWith` on the uppercased slot, so `"PREP (Sunday)"` counts
 * the same way `"PREP"` does — the slot carries parenthesised asides exactly as
 * every other slot on this screen does.
 */
const CHORE_SLOTS = ['PREP']

/** Whether this row's slot says it is a chore. See `resolveSavedMeal` rule 2. */
function isChoreRow(title: string): boolean {
  const { slot } = splitSlot(title)
  if (slot === null) return false
  const upper = slot.toUpperCase()
  return CHORE_SLOTS.some((chore) => upper.startsWith(chore))
}

/**
 * The `saved_meals` row a planned item is naming, or null.
 *
 * A plan item holds no ingredients: `plan_items.title` names a meal and
 * `details.macros` is prose someone typed, frequently truncated. The recipe and
 * the true numeric macros are in `saved_meals`, and there is no foreign key
 * between the two tables — the coach writes both, by name. So the join has to be
 * made here, and the only honest way to make it is conservatively.
 *
 * `kind` gates both rules. `/plan` returns workouts alongside meals and this
 * screen renders both, so rule 2 below is otherwise offered every workout title
 * in the response — and a library name occurring in one ("…then D1 Smash burger
 * bowl") would hang a recipe and five macro figures off a lifting session. The
 * explicit-key path is gated too: a `details.saved_meal` on a workout row is a
 * coaching mistake, not a fact worth rendering.
 *
 * `kind` cannot reach the Sunday chore row, though, because that row is stored
 * as `kind='meal'` — all 21 live `plan_items` are. So rule 2 was offered "PREP:
 * batch cook D1 Smash burger bowl x2 (35 min)", resolved D1, and put that meal's
 * recipe and `KCAL 512 / PROT 44` on a grocery run, while `supersededKeys`
 * deleted the row's own `MACROS` and `SAVED MEAL` lines in exchange. A row that
 * says "go to the shop" ends up stating, as a fact, that it is a 512 kcal meal.
 * `CHORE_SLOTS` skips rule 2 for it.
 *
 * Note how narrow that is: rule 1 still runs on a chore row. A coach who
 * deliberately points a prep row at `D1 Smash burger bowl` is saying "this is the
 * thing you are batch-cooking", and the recipe is genuinely useful standing at
 * the counter with a pan. It is the ACCIDENTAL substring match that puts a
 * calorie count on a grocery run, and only that is refused.
 *
 * Two rules, in order:
 *
 * 1. `details.saved_meal`, when it is a string, is an exact name. Names are
 *    `UNIQUE (user, name)` on the table, so an exact hit cannot be the wrong
 *    meal. It is trimmed before comparing, matching `queries.saved_meals`, which
 *    strips the name it is asked for; no stored name has surrounding whitespace,
 *    so trimming can only rescue a key, never loosen a match.
 *
 *    A key that hits nothing returns NULL and does not fall through to the title.
 *    That is the whole point of doing this in a tested function: an explicit
 *    pointer at a meal that has since been renamed or deleted is dangling, and
 *    quietly matching something else instead would put another meal's
 *    ingredients on the card, looking exactly as correct as a real match.
 *
 * 2. Otherwise — and only for a row whose slot is not a chore — the LONGEST
 *    saved-meal name appearing anywhere in the title ON A WORD BOUNDARY wins,
 *    case-insensitively. Most rows carry no key at all and their titles read
 *    `"COOK NIGHT (20 min): D1 Smash burger bowl - MAKE DOUBLE"` or `"NO COOKING:
 *    leftover D2 Korean beef bowl"`, so a substring is all there is to go on.
 *    Longest-wins is not a tie-break nicety: a library holding both `"Burrito
 *    bowl"` and `"L1 Burrito bowl"` would otherwise let the shorter, less
 *    specific name shadow the longer one it is contained in, and shadowing always
 *    resolves to the wrong meal. Ties keep the first in the list, which is the
 *    server's order, so the result never depends on how the array was built.
 *
 *    The boundary is what stops a bare `includes`. Names are arbitrary non-blank
 *    strings, so a meal called `"Egg"` matches the `veggie` in `"veggie
 *    scramble"` and hangs its recipe and its macro strip off an unrelated
 *    breakfast — while `supersededKeys` takes that card's own `macros` prose away
 *    in exchange. Latent rather than live: all 16 names in the library today are
 *    multi-word and code-prefixed (`"B1 Protein yogurt bowl"`, `"L3 Turkey
 *    chili"`) and none of them occurs inside a word. It is the next one-word meal
 *    someone saves that trips it.
 *
 * Anything else is null, and null renders the card exactly as it did before this
 * join existed.
 *
 * Exported for `Plan.test.ts`.
 */
export function resolveSavedMeal(item: PlanItem, meals: SavedMeal[]): SavedMeal | null {
  if (item.kind !== 'meal') return null

  const key = item.details?.[SAVED_MEAL_KEY]
  if (typeof key === 'string') {
    const wanted = key.trim()
    return meals.find((m) => m.name === wanted) ?? null
  }

  // Rule 2 only. An explicit key above is the coach's own pointer and is worth
  // honouring on a batch-cook row; it is the accidental match that is not.
  if (isChoreRow(item.title)) return null

  const title = item.title.toLowerCase()
  let best: SavedMeal | null = null
  for (const m of meals) {
    // A blank name cannot exist (`SavedMealIn` refuses one) but would substring-
    // match every title if it did, so it is never a candidate.
    if (!m.name) continue
    if (!occursOnBoundary(title, m.name.toLowerCase())) continue
    if (best === null || m.name.length > best.name.length) best = m
  }
  return best
}

/** The five macros in the order they are read, with the labels the card shows. */
const MACRO_FIGURES: { field: keyof SavedMeal; label: string; grams: boolean }[] = [
  { field: 'calories', label: 'KCAL', grams: false },
  { field: 'protein_g', label: 'PROT', grams: true },
  { field: 'carbs_g', label: 'CARB', grams: true },
  { field: 'fat_g', label: 'FAT', grams: true },
  { field: 'fiber_g', label: 'FIB', grams: true },
]

/**
 * A saved meal's real macros as up to five compact figures.
 *
 * Every one of the five columns is nullable, and a null is "nobody counted this",
 * never zero — so a missing macro produces no figure rather than a `0`. Printing a
 * zero would invent a fact about the meal, which is the one thing a screen joining
 * two tables the coach wrote by hand must not do. A stored 0 IS rendered: that is
 * a counted zero, and black coffee really does have no fat.
 *
 * Rounded to one decimal because the columns are `NUMERIC` and arrive here through
 * `float()`, which is enough to turn 9.7 into 9.699999999 on the way. `String` of
 * the rounded number drops a trailing `.0` on its own, so 337.0 reads `337`.
 *
 * Exported for `Plan.test.ts`.
 */
export function macroFigures(saved: SavedMeal): { label: string; value: string }[] {
  const figures: { label: string; value: string }[] = []
  for (const { field, label, grams } of MACRO_FIGURES) {
    const value = saved[field]
    if (typeof value !== 'number') continue
    figures.push({ label, value: `${Math.round(value * 10) / 10}${grams ? 'G' : ''}` })
  }
  return figures
}

/**
 * Which day the screen opens on.
 *
 * Today, when today has a plan. Otherwise the next planned day — a rest day, or
 * a week the coach only half-wrote, should show what is coming rather than what
 * was already eaten. Only when the entire plan is behind the user does it fall
 * back to the last planned day, which is then the least stale thing there is.
 *
 * `serverToday` is `/targets.as_of` and may be null, because that read is soft:
 * it is allowed to fail without taking the screen down. Null means "this screen
 * does not know what day it is", and the honest answer is the first planned day,
 * which makes no claim about the date at all. This is why nothing here consults
 * `new Date()`: a browser clock a day out would select the wrong day and mark
 * the wrong chip TODAY, and unlike a failed read it would do so confidently.
 *
 * ISO dates compare correctly as strings, so no Date is constructed. The order
 * of `dates` is `groupByDate`'s — the server's — and is read as given rather
 * than assumed ascending.
 *
 * Exported for `Plan.test.ts`.
 */
export function pickInitialDay(dates: string[], serverToday: string | null): string | null {
  if (dates.length === 0) return null
  if (serverToday === null) return dates[0]
  if (dates.includes(serverToday)) return serverToday
  return dates.find((date) => date > serverToday) ?? dates[dates.length - 1]
}

/**
 * Which day is on screen: the chip the user pressed, or the default once there
 * is one to compute.
 *
 * The default is not available immediately, and pretending otherwise is what
 * made the screen jump. `/plan` and `/targets` are two reads that settle at
 * different times, and the default is derived at render — so with `/plan` back
 * first, `pickInitialDay(dates, null)` returned the FIRST planned day, the cards
 * drew it, and a beat later the server date landed and the whole screen moved to
 * today. `todaySettled` is the missing fact: not "do we know the date" but "have
 * we finished trying to find out". Before it, there is no default at all and the
 * caller shows its loading note.
 *
 * `chosen` is checked first and does not wait for anything. A press is not a
 * default, and a user who taps a chip during the wait has said which day they
 * want; showing them a loading note instead would be the screen ignoring them
 * over a read they never asked for.
 *
 * Exported for `Plan.test.ts`.
 */
export function pickSelectedDay(
  chosen: string | null,
  dates: string[],
  serverToday: string | null,
  todaySettled: boolean,
): string | null {
  if (chosen !== null) return chosen
  if (!todaySettled) return null
  return pickInitialDay(dates, serverToday)
}

/**
 * The note shown when the screen has given up on learning today's date.
 *
 * `/targets` is a soft read and is allowed to fail; when it does, `serverToday`
 * is null forever and `pickInitialDay` opens on the first planned day, which in
 * a fully-written fortnight is up to a week behind. That is the honest choice —
 * nothing on this screen may consult `new Date()`, for the reasons `monoDate`,
 * `chipParts` and `pickInitialDay` all give — but making it silently answers
 * "what am I eating today" with last Monday, and no chip is marked TODAY to hint
 * why. So the screen says so instead.
 *
 * `todayUnknown` is that read having CONCLUDED without a date — resolved with no
 * `as_of`, or rejected. It is emphatically NOT `todaySettled`, and the two were
 * wrongly fused. `todaySettled` also flips when the grace timer expires, which
 * on a cold container or a slow phone means "still waiting" rather than "will
 * never know" — and the screen printed TODAY'S DATE UNKNOWN at the grace, which
 * was false at the moment it was printed, then moved to today a second later
 * when the answer landed. Two different questions: the gate asks whether to keep
 * waiting before choosing a day, the note asserts a fact about the world.
 *
 * So the grace can move the screen but can never make it say anything. A slow
 * answer produces one correction backed by a real date and no false sentence; a
 * failure produces the note immediately and truthfully; a request that hangs
 * forever produces the first planned day in silence, which is the pre-gate
 * behaviour and the rarest of the three.
 *
 * The wording follows what is actually on screen. While the user is still on the
 * default it names the fallback; once a chip has been pressed the screen is no
 * longer showing the first planned day and stops claiming to be, but the date is
 * still unknown and the missing TODAY marker still needs explaining.
 *
 * Exported for `Plan.test.ts`.
 */
export function unknownDateNote(todayUnknown: boolean, chosen: string | null): string | null {
  if (!todayUnknown) return null
  return chosen === null
    ? 'TODAY’S DATE UNKNOWN ✦ SHOWING THE FIRST PLANNED DAY'
    : 'TODAY’S DATE UNKNOWN ✦ NO DAY IS MARKED TODAY'
}

/* ── Chips ───────────────────────────────────────────────────────────────── */

/** The number of space-separated fields `monoDate` produces: "TUE AUG 25". */
const MONO_FIELDS = 3

/**
 * A date chip's two lines: the weekday over the day number.
 *
 * Derived from `monoDate` rather than from a Date of its own, and that is the
 * whole point of the function. `new Date('2026-08-01')` is UTC midnight, which
 * is 2026-07-31 everywhere west of Greenwich — which is everywhere Milo runs —
 * so a chip built that way is labelled the day before the meals under it.
 * `monoDate` already builds its Date from the split parts to avoid exactly that,
 * so this reuses it instead of repeating the fix and risking half a fix.
 *
 * The day number keeps `monoDate`'s leading zero. A strip reading 22 24 25 27 29
 * and one reading 01 03 05 line up in the same mono column; stripping the zero
 * would make the chips change width as the month turned over.
 *
 * `monoDate` hands back the raw string uppercased when it cannot parse the date.
 * A chip still has to render something, so that string goes in the prominent
 * slot rather than being dropped into an empty chip nobody can click with
 * confidence.
 *
 * Exported for `Plan.test.ts`.
 */
export function chipParts(iso: string): { weekday: string; day: string } {
  const label = monoDate(iso)
  const parts = label.split(' ')
  if (parts.length !== MONO_FIELDS) return { weekday: '', day: label }
  return { weekday: parts[0], day: parts[2] }
}

/**
 * Where the chip strip has to be scrolled to put one chip in the middle of it.
 *
 * The strip is 15 chips of `min-width: 58px` that `flex: 0 0 auto` refuses to
 * shrink — near 1000px of content in a 390px phone — so it opens scrolled hard
 * left, on last week, while the cards below show today. The selected chip has to
 * be brought to the user rather than the user to the chip.
 *
 * Centred rather than merely made visible: the strip is a week either side of
 * today and the chips next to the selected one are the ones a user reaches for,
 * so an edge-aligned chip hides half of what they are about to press.
 *
 * Both clamps are the point of the function. An uncorrected centre for the first
 * chip is negative and for the last one is past the end, and either would ask
 * the strip for a scroll position it cannot hold — the browser silently clamps,
 * but the arithmetic that produced it was wrong, and clamping here is what makes
 * the wrong answer visible to a test. `Math.max(0, …)` on the ceiling covers the
 * half-written week whose chips already fit: the content is narrower than the
 * view, there is nowhere to scroll, and the answer is 0.
 *
 * Exported for `Plan.test.ts`.
 */
export function centeredScrollLeft(
  chipLeft: number,
  chipWidth: number,
  viewWidth: number,
  scrollWidth: number,
): number {
  const centered = chipLeft + chipWidth / 2 - viewWidth / 2
  const furthest = Math.max(0, scrollWidth - viewWidth)
  return Math.min(Math.max(centered, 0), furthest)
}

/* ── Loading ─────────────────────────────────────────────────────────────── */

/**
 * How long the screen waits for `/targets` before choosing a day without it.
 *
 * The gate has to be bounded rather than absolute. `api.ts` sets no request
 * timeout anywhere, so a hung `/targets` never resolves and never rejects — an
 * unbounded wait would leave PLAN reading LOADING… forever over a read that is
 * deliberately soft, turning the one endpoint this screen barely uses into the
 * one that can take it down.
 *
 * Three seconds because `/targets` is the LIGHTER of two requests that left
 * together: if it has not answered by then it is not about to, and the wait is
 * buying nothing. The request is not cancelled at the grace — a late answer
 * still moves the screen to today, which is a correction with a real date behind
 * it — so the only thing three seconds buys is a narrower window in which that
 * correction is visible at all. It is the one number that trades against the
 * true-hang case, where the screen reads LOADING… for the full grace and then
 * opens on the first planned day; that is the rarest of the three outcomes, and
 * a second and a half of it is worth not moving the screen under someone on an
 * ordinary cellular connection.
 *
 * The grace opens the gate and nothing else. It does not make the screen claim
 * the date is unknown — see `unknownDateNote`, which reads the read having
 * concluded, not this timer.
 */
const TODAY_GRACE_MS = 3000

type Load<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'error'; message: string }

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

/* ── Screen ──────────────────────────────────────────────────────────────── */

export function Plan() {
  const [plan, setPlan] = useState<Load<PlanItem[]>>({ status: 'loading' })

  /* The server's today, from `/targets.as_of`, and the ONLY answer this screen
     accepts to "what day is it". Null until the soft read lands, and null
     forever if it fails — see the effect. */
  const [serverToday, setServerToday] = useState<string | null>(null)

  /* Whether that read has finished TRYING — resolved, rejected, or run out of
     grace. Its ONE job is to unblock day selection so the screen cannot sit on
     LOADING… forever; it is not a claim about the world, because the grace
     branch makes it true while the request is still in flight. */
  const [todaySettled, setTodaySettled] = useState(false)

  /* Whether that read CONCLUDED without a date — no `as_of`, or rejected. The
     note's fact, and deliberately a different one: the grace timer never sets
     this, so a slow-but-alive `/targets` moves the screen when it answers and
     the screen never states, falsely, that it does not know the date. */
  const [todayUnknown, setTodayUnknown] = useState(false)

  /* The chip the user pressed, or null for "they have not pressed one yet".
     Deliberately not seeded with a day in an effect: the two reads settle at
     different times, and an effect that re-derived the selection when the second
     one landed would yank the user off a day they had already chosen. Null keeps
     "no choice made" and "chose the day that happens to be the default" distinct
     facts, and the choice below is derived at render rather than stored. */
  const [chosen, setChosen] = useState<string | null>(null)

  /* The saved-meal library, joined to each item by `resolveSavedMeal`. Empty
     until the soft read lands and empty forever if it fails, which resolves every
     item to null and renders the cards exactly as they were before the join
     existed. A recipe is an enrichment; the plan is the screen. */
  const [savedMeals, setSavedMeals] = useState<SavedMeal[]>([])

  /* The chip strip's own scroll box, so the effect below can move it. */
  const strip = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal

    /* The floor under B, and only that. `api.ts` has no request timeout, so B's
       promise can hang forever; without this the screen would wait forever with
       it. It opens the day-selection gate and pointedly does NOT set
       `todayUnknown`: the request is still running, and saying the date is
       unknown while an answer may still arrive is a false statement the screen
       would then have to take back. */
    const grace = setTimeout(() => setTodaySettled(true), TODAY_GRACE_MS)

    /* A — the hard read. Everything on the screen is this response; if it fails
       there is a strip and an empty frame and nothing to put in them. */
    void (async () => {
      try {
        // No window: `/plan`'s own default is today−7 … today+7, computed in
        // MILO_TIMEZONE, which is exactly the fortnight this screen shows. Naming
        // the dates here would mean deriving them from this browser's clock and
        // so asking for the wrong fortnight for anyone not in the server's zone.
        const res = await getPlan({}, signal)
        if (signal.aborted) return
        setPlan({ status: 'ready', data: res.items })
      } catch (error) {
        if (isAbort(error) || signal.aborted) return
        setPlan({ status: 'error', message: errorMessage(error) })
      }
    })()

    /* B — soft, and caught to null so it can never take the screen down. Its one
       job is `as_of`; a plan is perfectly readable without knowing which day is
       today, it just opens on the first planned day and marks no chip. Failing
       the whole screen because the macro-target endpoint is down would be a
       screen that breaks over a fact it barely uses.

       Soft, but no longer invisible: until this settles the screen picks NO day,
       because a default derived while it was still in flight is the first planned
       day, and drawing that only to move to today a beat later is the jump this
       gate removes.

       Two facts come out of it, not one. `todaySettled` opens the gate. Both a
       rejection and a 200 with no `as_of` are the read concluding that it cannot
       say what day it is, and only THOSE set `todayUnknown` — the fact the note
       is allowed to speak from. The request is deliberately not aborted when the
       grace expires: an honest two-second answer on a phone is worth having, and
       throwing it away would mark no chip TODAY for the rest of the session over
       a connection that was merely slow. */
    void (async () => {
      const targets = await getTargets(signal).catch(() => null)
      if (signal.aborted) return
      clearTimeout(grace)
      const asOf = targets?.as_of ?? null
      setServerToday(asOf)
      setTodaySettled(true)
      setTodayUnknown(asOf === null)
    })()

    /* C — soft, for the same reason B is, and caught to an empty library rather
       than to a failure state. This is what turns "B1 Protein yogurt bowl" into
       the ingredients someone cooks from and into real macros instead of the
       coach's truncated `details.macros` prose. It is also the one read on this
       screen the user cannot notice missing: without it every card falls back to
       what it always showed. Failing the plan over it would be a screen that
       breaks over an enrichment. */
    void (async () => {
      const library = await getSavedMeals({}, signal).catch(() => null)
      if (signal.aborted) return
      setSavedMeals(library?.saved_meals ?? [])
    })()

    return () => {
      clearTimeout(grace)
      controller.abort()
    }
    /* Empty deps, NOT `[epoch]`. `useShellSync`'s epoch is the FatSecret sync's
       refresh signal, and nothing on this screen is FatSecret-backed — the plan
       is written by the coach over MCP and a nutrition sync cannot change a row
       of it. Re-reading on every sync would be one pointless request per page
       load, and the reload would blow away the day the user had scrolled to.
       Journal.tsx sets the same precedent for a screen that does not sync. */
  }, [])

  const items = plan.status === 'ready' ? plan.data : []
  const days = groupByDate(items)
  const dates = days.map((group) => group.date)

  /* An explicit choice always wins; the fallback is recomputed as the reads land
     and so cannot leave the screen pointing at a day that no longer exists. */
  const selected = pickSelectedDay(chosen, dates, serverToday, todaySettled)
  const day = days.find((group) => group.date === selected) ?? null
  const dayTotal = day ? dayTotalOf(day.items) : null

  /* There are days to show and no day picked yet, which `pickSelectedDay` only
     returns while the server date is still in flight. Held as its own fact
     rather than re-testing `todaySettled` below, so the loading note and the
     selection can never disagree about whether a day has been chosen. */
  const awaitingToday = dates.length > 0 && selected === null
  const dateNote = unknownDateNote(todayUnknown, chosen)

  /* Bring the selected chip into the middle of the strip. Fifteen chips that
     cannot shrink are around 1000px of content, so on a phone the strip opens
     scrolled hard left — a week ago — with today's meals underneath it.

     `scrollLeft`, not `scrollIntoView`: the latter is allowed to scroll every
     ancestor, so `block: 'nearest'` still moves the PAGE vertically when the
     strip is not fully in view, and on iOS it fights the smooth-scroll it starts
     itself. Assigning `scrollLeft` can only move this box, and only sideways.

     The chip is found by the attribute that already says which one is on, rather
     than by a second ref map that could disagree with it. Position is measured
     from the rects plus the current `scrollLeft`, because `offsetLeft` is
     relative to an offset parent this box does not control.

     `[selected]` alone, deliberately. A user who has scrolled the strip by hand
     has not selected anything, and an effect that re-ran on the reads landing
     would scroll the strip back out from under them. */
  useEffect(() => {
    const box = strip.current
    if (!box) return
    const chip = box.querySelector<HTMLElement>('[aria-pressed="true"]')
    if (!chip) return

    const chipBox = chip.getBoundingClientRect()
    const stripBox = box.getBoundingClientRect()
    const chipLeft = chipBox.left - stripBox.left + box.scrollLeft
    box.scrollLeft = centeredScrollLeft(chipLeft, chipBox.width, box.clientWidth, box.scrollWidth)
  }, [selected])

  return (
    <>
      {plan.status === 'error' ? (
        <Alert tone="danger" role="alert" title="Can’t read your plan.">
          {plan.message}
        </Alert>
      ) : null}

      <div className="milo-heading-row">
        {/* The SELECTED day, not today — the heading has to name what is on
            screen, or a user reading Thursday's meals under a Tuesday heading
            would eat the wrong thing. An em dash while there is nothing
            selected: this screen never guesses a date. */}
        <h2 className="milo-heading">Plan / {selected ? monoDate(selected) : '—'}</h2>
      </div>

      {dates.length > 0 ? (
        /* Plain buttons in a plain row. Deliberately NOT a second `role="tablist"`
           — the shell already owns the page's one tablist, and a nested one
           would put two arrow-key regions on the same screen with different
           meanings. `aria-pressed` says which chip is on, the same way the meal
           chips on LOG do. */
        <div
          className="milo-plan-strip"
          role="group"
          aria-label="Planned days"
          ref={strip}
        >
          {dates.map((date) => {
            const { weekday, day: dayNumber } = chipParts(date)
            const isToday = date === serverToday
            return (
              <button
                key={date}
                type="button"
                className="milo-chip milo-plan-chip"
                aria-pressed={date === selected}
                // The chip's own text is "TUE / 25", which read aloud on its own
                // does not say which month or year, and TODAY is a colour-free
                // marker that still needs saying.
                aria-label={`${monoDate(date)}${isToday ? ' (today)' : ''}`}
                onClick={() => setChosen(date)}
              >
                <span className="milo-plan-chip-weekday" aria-hidden="true">
                  {weekday}
                </span>
                <span className="milo-plan-chip-day" aria-hidden="true">
                  {dayNumber}
                </span>
                {isToday ? (
                  <span className="milo-plan-today" aria-hidden="true">
                    TODAY
                  </span>
                ) : null}
              </button>
            )
          })}
        </div>
      ) : null}

      {/* Directly under the strip, where someone reading the chips for a TODAY
          marker that is not there will find the reason. Only alongside a strip:
          with nothing planned, the date the screen does not know is not a fact
          about anything on it. */}
      {dateNote !== null && dates.length > 0 ? (
        <div className="milo-note milo-note--sm">{dateNote}</div>
      ) : null}

      {/* `awaitingToday` shares this note with the hard read on purpose. Both
          are the same sentence — the screen cannot yet say which day it is
          showing — and giving the soft read its own wording would put a second
          kind of waiting on screen for a difference the user cannot act on. */}
      {plan.status === 'loading' || awaitingToday ? (
        <div className="milo-note milo-note--sm">LOADING…</div>
      ) : day === null ? (
        /* Empty and broken are different sentences. "Nothing is planned" is an
           invitation and is only true if the read actually succeeded; saying it
           after a 500 would tell the user their coach had forgotten them. Same
           split Trends.tsx makes for its chart. */
        <div className="milo-note milo-note--sm">
          {plan.status === 'error'
            ? 'PLAN UNAVAILABLE'
            : 'NO MEALS PLANNED ✦ ASK MILO TO PLAN YOUR WEEK'}
        </div>
      ) : (
        <>
          <div className="milo-plan-list">
            {day.items.map((item) => {
              const { slot, body } = splitSlot(item.title)
              /* The join, then what it produced, then the rows whatever it
                 produced makes redundant — in that order, because a row is only
                 dropped against the thing that actually replaces it. A card whose
                 meal resolved to no figures and no recipe keeps its `macros`
                 prose, which is then the only macro figure it has. */
              const saved = resolveSavedMeal(item, savedMeals)
              const figures = saved ? macroFigures(saved) : []
              const recipe = recipeText(saved)
              const rows = detailRows(item.details, supersededKeys(saved, figures))
              return (
                <Window key={item.id} title={slot ?? 'PLAN'} shadow="m">
                  <div className="milo-plan-item">
                    <div className="milo-plan-body">{body}</div>

                    {item.status !== 'planned' || item.kind === 'workout' ? (
                      <div className="milo-plan-tags">
                        {/* PLANNED gets no pill: it is the default state of
                            every row here and a pill on all of them would say
                            nothing while crowding out the two that do. */}
                        {item.status === 'done' ? <Pill color="mint">DONE</Pill> : null}
                        {item.status === 'skipped' ? <Pill color="paper">SKIPPED</Pill> : null}
                        {/* Only meals exist today, but `/plan` returns both kinds
                            and a tab called PLAN that silently dropped the
                            workouts would be lying by omission. */}
                        {item.kind === 'workout' ? <Pill color="blue">WORKOUT</Pill> : null}
                      </div>
                    ) : null}

                    {/* The recipe, as body prose rather than as a mono detail
                        row: this is a sentence someone reads standing at a
                        counter, not a code. No description renders nothing — a
                        meal saved without one, or with only whitespace in one,
                        is not a meal with an empty recipe. `recipeText` decides
                        that, and `supersededKeys` above asked it the same
                        question, so the row it dropped is the row this
                        replaces. */}
                    {recipe !== null ? <div className="milo-plan-recipe">{recipe}</div> : null}

                    {/* The true macros, from `saved_meals`' own numeric columns.
                        Up to five: a macro nobody counted is absent, never a 0. */}
                    {figures.length > 0 ? (
                      <div className="milo-plan-macros">
                        {figures.map((figure) => (
                          <div className="milo-plan-figure" key={figure.label}>
                            <span className="milo-plan-figure-label">{figure.label}</span>
                            <span className="milo-plan-figure-value">{figure.value}</span>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {rows.length > 0 ? (
                      <dl className="milo-plan-rows">
                        {rows.map((row) => (
                          <div className="milo-plan-row" key={row.key}>
                            <dt className="milo-plan-label">{row.label}</dt>
                            <dd className="milo-plan-value">{row.value}</dd>
                          </div>
                        ))}
                      </dl>
                    ) : null}

                    {item.feedback.length > 0 ? (
                      <div className="milo-plan-feedback">
                        {item.feedback.map((note) => (
                          <div key={note.id}>
                            <div className="milo-note milo-note--sm">
                              {/* The sentiment is uppercased from the wire value
                                  rather than borrowed from Journal's label map,
                                  which would pull that whole screen and its
                                  stylesheet into this bundle for four words. */}
                              FEEDBACK{note.sentiment ? ` ✦ ${labelOf(note.sentiment)}` : ''}
                            </div>
                            <div className="milo-plan-note-body">{note.content}</div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </Window>
              )
            })}
          </div>

          {dayTotal !== null ? (
            <div className="milo-total milo-plan-footer">
              <span className="milo-total-label">DAY TOTAL</span>
              <span className="milo-total-value">{dayTotal}</span>
            </div>
          ) : null}
        </>
      )}
    </>
  )
}

export default Plan
